import _ from "lodash";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import { getChatDetailsQuery } from "MessagingV2/modules/MessagingQuery";
import { withChatOptions } from "MessagingV2/providers/ChatOptionsProvider";

import { getChatDetailsFromCache } from "MessagingV2/modules/MessagingGraphqlHelper";
import {
  CHAT_TYPE,
  CHAT_TYPE_BE,
  getChatDetailsQueryVariables,
  getChatSessionKey,
  getIsDepartmentId,
} from "MessagingV2/modules/Utils";
import { generateSuggestions } from "MessagingV2/modules/MessagingModule";
import { getOrganizationId } from "modules/CommonSelectors";
import { isIdEqual } from "Utils/idHelpers";

import Header from "./Header";
import MessagesAndInputContainer from "./MessagesAndInputContainer";
import classes from "./ChatContainer.scss";
import DepartmentDetailContainer from "./DepartmentDetailContainer";
import InitAddMemberModal from "./InitAddMemberModal";
import InitFormRespondedModal from "./InitFormRespondedModal";

const SESSION_SET = new Set();

const ChatContainer = props => {
  const {
    chatId,
    screenType,
    chatDetails = {},
    unreadMessageCount,
    onBackClickFromWidget = () => {},
    lastReadMessage,
    userId,
    onCloseChatContainer = () => {},
  } = props;

  const {
    isMuted = false,
    description,
    profileImageData = {},
    otherUsers = [],
    users = [],
    type,
    lastActiveAt,
    isArchived: isArchivedChatDetail,
    label: channelLabel,
    viewOnlyRoles,
    viewOnlyUsers,
    personalChatReceiverUser,
    createdBy,
    isPersonalChatPaid,
    bannerMessage,
    archivedBy,
    isConnectEnabledForReceiver,
    isMembersListVisible,
    memberListHiddenRoles,
  } = chatDetails;

  const chatType =
    type === CHAT_TYPE_BE.CHAT_PERSONAL ? CHAT_TYPE.CHAT : CHAT_TYPE.CHANNEL;

  const isArchived = _.get(personalChatReceiverUser, "isArchived", false);
  const isUserCreator = isIdEqual(createdBy.id, userId);

  return (
    <>
      <div className={classes.rootContainer}>
        <Header
          chatType={chatType}
          bannerMessage={bannerMessage}
          isArchived={isArchived}
          isArchivedChatDetail={isArchivedChatDetail}
          isMuted={isMuted}
          isMembersListVisible={isMembersListVisible}
          memberListHiddenRoles={memberListHiddenRoles}
          users={users}
          personalChatReceiverUser={personalChatReceiverUser}
          onBackClickFromWidget={onBackClickFromWidget}
          onCloseChatContainer={onCloseChatContainer}
          description={description}
          channelLabel={channelLabel}
          conversationType={type}
          viewOnlyRoles={viewOnlyRoles}
          screenType={screenType}
          isUserCreator={isUserCreator}
          createdBy={createdBy}
          profileImage={profileImageData?.url}
          profileIcon={profileImageData?.icon}
        />
        <MessagesAndInputContainer
          description={description}
          bannerMessage={bannerMessage}
          chatId={chatId}
          chatType={chatType}
          screenType={screenType}
          otherUsers={otherUsers}
          lastActiveAt={lastActiveAt}
          users={users}
          isArchived={isArchived}
          isArchivedChatDetail={isArchivedChatDetail}
          isPersonalChatPaid={isPersonalChatPaid}
          // following props needed for graphql query
          conversationType={type}
          unreadMessageCount={unreadMessageCount}
          lastReadMessage={lastReadMessage}
          channelLabel={channelLabel}
          viewOnlyRoles={viewOnlyRoles}
          viewOnlyUsers={viewOnlyUsers}
          isUserCreator={isUserCreator}
          createdBy={createdBy}
          archivedBy={archivedBy}
          personalChatReceiverUser={personalChatReceiverUser}
          isConnectEnabledForReceiver={isConnectEnabledForReceiver}
        />
      </div>
      <InitAddMemberModal
        chatId={chatId}
        channelLabel={channelLabel}
        conversationType={type}
        users={users}
      />
      <InitFormRespondedModal chatId={chatId} />
    </>
  );
};

const ChatContainerWithLoader = compose(
  graphql(getChatDetailsQuery, {
    name: "getChatDetails", // fetch meta details related to chat id
    options: ({ chatId, userId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ...getChatDetailsQueryVariables({ chatId, userId }),
        portalType: "PLANNER",
      },
    }),
    props({ getChatDetails, ownProps: { chatId, userId } }) {
      const chatDetails = getChatDetailsFromCache(getChatDetails.variables);

      const updatedChatDetails = {
        ...chatDetails,
        otherUsers: _.filter(
          chatDetails.users,
          user => !isIdEqual(user.id, userId)
        ),
      };
      const isLoading = _.includes([1, 2], getChatDetails["networkStatus"]);

      // This SESSION_SET is used to ignore persisted data for first mount(after refresh)
      // TODO: should be handled with the combination of nextFetchPolicy and fetchPolicy
      // and with direct use of data from HOC response. But for that we need to have
      // the Apollo Client V3 version in place live for every school on every environment.
      const chatSessionKey = getChatSessionKey({ chatId });
      if (
        !isLoading &&
        !_.isEmpty(chatDetails) &&
        !SESSION_SET.has(chatSessionKey)
      ) {
        SESSION_SET.add(chatSessionKey);
      }

      return {
        isLoading,
        isData: SESSION_SET.has(chatSessionKey) && !_.isEmpty(chatDetails),
        chatDetails: updatedChatDetails,
        unreadMessageCount: _.get(chatDetails, "unreadMessageCount", 0),
        lastReadMessage: _.get(chatDetails, "lastReadMessage"),
      };
    },
  }),
  withLoader
)(ChatContainer);

ChatContainerWithLoader.propTypes = {
  chatId: PropTypes.string.isRequired,
  screenType: PropTypes.string, // to show footer in search screen
  onBackClickFromWidget: PropTypes.func,
};

const ChatContainerWrapper = props => {
  const { chatId, generateSuggestions } = props;
  useEffect(() => {
    const isDepartmentId = getIsDepartmentId(chatId);
    // As soon as chatId changes, we need to generate suggestions
    // without waiting for the messages to load
    // if not department id
    if (!isDepartmentId && chatId) {
      generateSuggestions({ chatId });
    }
  }, [chatId, generateSuggestions]);

  const isDepartmentId = getIsDepartmentId(chatId);

  if (isDepartmentId) {
    return <DepartmentDetailContainer {...props} />;
  }

  return <ChatContainerWithLoader {...props} />;
};

const mapStateToProps = (state, ownProps) => {
  const userId = _.get(ownProps, "activeUserId");
  return {
    userId,
    organizationId: getOrganizationId(state),
  };
};

const mapDispatchToProps = { generateSuggestions };

export default compose(
  withChatOptions(),
  connect(mapStateToProps, mapDispatchToProps)
)(ChatContainerWrapper);
